import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '@/lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { UserPlus, Plus, Trash2 } from 'lucide-react';
import { FilterBar, SelectFilter, SearchInput, DateRangeFilter, ClearFilters } from '@/components/filters';
import { buildQuery, type DateRange } from '@/lib/filters';
import { StatTiles } from '@/components/StatTiles';
import { PageHeader } from '@/components/PageHeader';
import { Pagination, DEFAULT_PAGE_SIZE } from '@/components/Pagination';
import { StatusBadge } from '@/components/StatusBadge';

interface Partner {
  id: string;
  name: string;
  email: string;
  type: string;
  status: string;
  commissionRate: number;
  discountRate: number;
  createdAt?: number;
  created_at?: number;
}

function Partners() {
  const { t } = useTranslation();
  const [partners, setPartners] = useState<Partner[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);
  const [newPartner, setNewPartner] = useState({
    name: '',
    email: '',
    type: 'RESELLER',
    commissionRate: 0,
    discountRate: 0,
    status: 'ACTIVE',
  });

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState<DateRange>({});

  useEffect(() => {
    const id = setTimeout(fetchPartners, search ? 300 : 0);
    return () => clearTimeout(id);
  }, [search, typeFilter, statusFilter, dateRange.from, dateRange.to, page]);

  // A filter change invalidates the current page — otherwise you can sit on
  // page 5 of a result set that now has one page and see an empty table.
  useEffect(() => { setPage(1); }, [search, typeFilter, statusFilter, dateRange.from, dateRange.to]);

  const clearFilters = () => {
    setSearch('');
    setTypeFilter('all');
    setStatusFilter('all');
    setDateRange({});
  };

  const fetchPartners = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';
      const qs = buildQuery({
        limit: DEFAULT_PAGE_SIZE,
        page,
        search,
        type: typeFilter,
        status: statusFilter,
        from: dateRange.from,
        to: dateRange.to,
      });
      const response = await fetch(`${apiUrl}/api/partners${qs}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setPartners(data.data?.partners || data.data || []);
        setTotal(data.data?.total ?? data.total ?? 0);
      }
    } catch (error) {
      console.error('Failed to fetch partners:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreatePartner = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      // Transform camelCase to snake_case for backend compatibility
      const payload = {
        name: newPartner.name,
        email: newPartner.email,
        type: newPartner.type,
        status: newPartner.status,
        commission_rate: newPartner.commissionRate,
        discount_rate: newPartner.discountRate,
      };

      const response = await fetch(`${apiUrl}/api/partners`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setIsCreateDialogOpen(false);
        setNewPartner({
          name: '',
          email: '',
          type: 'RESELLER',
          commissionRate: 0,
          discountRate: 0,
          status: 'ACTIVE',
        });
        fetchPartners();
      }
    } catch (error) {
      console.error('Failed to create partner:', error);
    }
  };

  const handleEditPartner = async () => {
    if (!selectedPartner) return;

    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      // Transform camelCase to snake_case for backend compatibility
      const payload = {
        name: selectedPartner.name,
        email: selectedPartner.email,
        type: selectedPartner.type,
        status: selectedPartner.status,
        commission_rate: selectedPartner.commissionRate,
        discount_rate: selectedPartner.discountRate,
      };

      const response = await fetch(`${apiUrl}/api/partners/${selectedPartner.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setIsEditDialogOpen(false);
        setSelectedPartner(null);
        fetchPartners();
      }
    } catch (error) {
      console.error('Failed to update partner:', error);
    }
  };

  const openEditDialog = (partner: Partner) => {
    setSelectedPartner({ ...partner });
    setIsEditDialogOpen(true);
  };

  const openDeleteDialog = (partner: Partner) => {
    setSelectedPartner(partner);
    setIsDeleteDialogOpen(true);
  };

  const handleDeletePartner = async () => {
    if (!selectedPartner) return;

    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/partners/${selectedPartner.id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setIsDeleteDialogOpen(false);
        setSelectedPartner(null);
        fetchPartners();
      }
    } catch (error) {
      console.error('Failed to delete partner:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="w-full px-4 py-6 sm:px-6 lg:px-8">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">Loading...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 px-4 py-6 sm:px-6 lg:px-8">
      <PageHeader
        eyebrow="Partners"
        icon={UserPlus}
        title="Partners"
        subtitle="Manage resellers, distributors, OEM, and affiliates"
        actions={(
          <Button onClick={() => setIsCreateDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Partner
          </Button>
        )}
      />

      <StatTiles stats={[
        { label: t('partners.stats.matching', { defaultValue: 'Matching results' }), value: total },
        { label: t('partners.stats.active', { defaultValue: 'Active on page' }), value: partners.filter((p) => p.status === 'ACTIVE').length, color: 'text-success' },
        { label: t('partners.stats.inactive', { defaultValue: 'Inactive on page' }), value: partners.filter((p) => p.status === 'INACTIVE').length },
        { label: t('partners.stats.reseller', { defaultValue: 'Reseller on page' }), value: partners.filter((p) => p.type === 'RESELLER').length },
        { label: t('partners.stats.distributor', { defaultValue: 'Distributor on page' }), value: partners.filter((p) => p.type === 'DISTRIBUTOR').length },
        { label: t('partners.stats.referral', { defaultValue: 'Referral on page' }), value: partners.filter((p) => p.type === 'REFERRAL').length },
      ]} />

      <Card>
        <CardHeader>
          <CardTitle>All Partners</CardTitle>
          <CardDescription>
            {total} partner{total !== 1 ? 's' : ''} in your network
          </CardDescription>
        </CardHeader>
        <CardContent>
          <FilterBar>
            <SearchInput value={search} onChange={setSearch} placeholder="Name, email or company…" />
            <SelectFilter
              label="Type"
              value={typeFilter}
              onChange={setTypeFilter}
              options={['RESELLER', 'DISTRIBUTOR', 'REFERRAL', 'OEM'].map((v) => ({ value: v, label: v }))}
            />
            <SelectFilter
              label="Status"
              value={statusFilter}
              onChange={setStatusFilter}
              options={['ACTIVE', 'INACTIVE'].map((v) => ({ value: v, label: v }))}
            />
            <DateRangeFilter label="Created" value={dateRange} onChange={setDateRange} />
            <ClearFilters onClick={clearFilters} />
          </FilterBar>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Commission</TableHead>
                <TableHead>Discount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {partners.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-muted-foreground">
                    No partners found
                  </TableCell>
                </TableRow>
              ) : (
                partners.map((partner) => (
                  <TableRow key={partner.id}>
                    <TableCell className="font-medium">{partner.name}</TableCell>
                    <TableCell>{partner.email}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="border-primary/20 bg-primary/10 text-primary">
                        {partner.type}
                      </Badge>
                    </TableCell>
                    <TableCell>{partner.commissionRate}%</TableCell>
                    <TableCell>{partner.discountRate}%</TableCell>
                    <TableCell>
                      <StatusBadge status={partner.status} />
                    </TableCell>
                    <TableCell>
                      {formatDate(partner.created_at || partner.createdAt)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={() => openEditDialog(partner)}>
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDeleteDialog(partner)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <Pagination page={page} pageSize={DEFAULT_PAGE_SIZE} total={total} onPageChange={setPage} label="partners" />
        </CardContent>
      </Card>

      {/* Create Partner Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Partner</DialogTitle>
            <DialogDescription>
              Create a new partner in your network
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={newPartner.name}
                onChange={(e) => setNewPartner({ ...newPartner, name: e.target.value })}
                placeholder="Partner Name"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={newPartner.email}
                onChange={(e) => setNewPartner({ ...newPartner, email: e.target.value })}
                placeholder="partner@example.com"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="type">Partner Type</Label>
              <Select
                value={newPartner.type}
                onValueChange={(value) => setNewPartner({ ...newPartner, type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="RESELLER">Reseller</SelectItem>
                  <SelectItem value="DISTRIBUTOR">Distributor</SelectItem>
                  <SelectItem value="OEM">OEM</SelectItem>
                  <SelectItem value="AFFILIATE">Affiliate</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="commissionRate">Commission Rate (%)</Label>
              <Input
                id="commissionRate"
                type="number"
                min="0"
                max="100"
                value={newPartner.commissionRate}
                onChange={(e) => setNewPartner({ ...newPartner, commissionRate: parseFloat(e.target.value) || 0 })}
                placeholder="0"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="discountRate">Discount Rate (%)</Label>
              <Input
                id="discountRate"
                type="number"
                min="0"
                max="100"
                value={newPartner.discountRate}
                onChange={(e) => setNewPartner({ ...newPartner, discountRate: parseFloat(e.target.value) || 0 })}
                placeholder="0"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={newPartner.status}
                onValueChange={(value) => setNewPartner({ ...newPartner, status: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ACTIVE">Active</SelectItem>
                  <SelectItem value="INACTIVE">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreatePartner} disabled={!newPartner.name || !newPartner.email}>
              Create Partner
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Partner Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Partner</DialogTitle>
            <DialogDescription>
              Update partner information
            </DialogDescription>
          </DialogHeader>
          {selectedPartner && (
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="edit-name">Name</Label>
                <Input
                  id="edit-name"
                  value={selectedPartner.name}
                  onChange={(e) => setSelectedPartner({ ...selectedPartner, name: e.target.value })}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-email">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={selectedPartner.email}
                  onChange={(e) => setSelectedPartner({ ...selectedPartner, email: e.target.value })}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-type">Partner Type</Label>
                <Select
                  value={selectedPartner.type}
                  onValueChange={(value) => setSelectedPartner({ ...selectedPartner, type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="RESELLER">Reseller</SelectItem>
                    <SelectItem value="DISTRIBUTOR">Distributor</SelectItem>
                    <SelectItem value="OEM">OEM</SelectItem>
                    <SelectItem value="AFFILIATE">Affiliate</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-commissionRate">Commission Rate (%)</Label>
                <Input
                  id="edit-commissionRate"
                  type="number"
                  min="0"
                  max="100"
                  value={selectedPartner.commissionRate}
                  onChange={(e) => setSelectedPartner({ ...selectedPartner, commissionRate: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-discountRate">Discount Rate (%)</Label>
                <Input
                  id="edit-discountRate"
                  type="number"
                  min="0"
                  max="100"
                  value={selectedPartner.discountRate}
                  onChange={(e) => setSelectedPartner({ ...selectedPartner, discountRate: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-status">Status</Label>
                <Select
                  value={selectedPartner.status}
                  onValueChange={(value) => setSelectedPartner({ ...selectedPartner, status: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ACTIVE">Active</SelectItem>
                    <SelectItem value="INACTIVE">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditPartner}>
              Update Partner
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Partner Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Partner</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {selectedPartner?.name}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeletePartner}>
              Delete Partner
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Partners;
